import type { BasicCouponFragment, CompleteStoreFragment } from '@drycut/graphql';

import { BookingPayment, ControlledCheckBox } from '@drycut/controlled-components';
import { ActionButtons, BigDisplayField, InfoButton, Para, Stack } from '@drycut/ui-components';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { computeBookingPrice } from '../../../computeBookingPrice';
import { CouponInsert } from '../../../CouponInsert';
import { useBookingContext } from '../../BookingProvider';
import StepContainer from '../../StepContainer';
import CancelButton from '../CancelButton';
import StepWaitingTime from '../StepWaitingTime';

type Props = {
  onCancel: () => void;
  store: CompleteStoreFragment;
};

export function PaymentStep({ store, onCancel }: Props): React.ReactElement {
  const [selectedCoupon, setSelectedCoupon] = useState<BasicCouponFragment>();
  const { manual } = useBookingContext();

  const customerName = useWatch({ name: 'name' });
  const phoneNumber = useWatch({ name: 'phone' });
  const silentBooking = useWatch({ name: 'silentBooking' });
  const kidCustomer = useWatch({ name: 'kidCustomer' });

  const computedPrice = computeBookingPrice({
    service: store.service,
    coupon: selectedCoupon,
    kidCustomer,
  });

  return (
    <StepContainer store={store}>
      <StepWaitingTime store={store} />

      <Stack gap={2} className="items-center">
        <Para>Jetzt Buchen:</Para>
        <BigDisplayField content={computedPrice} suffix="CHF" />

        {Boolean(store.service?.kidsPrice) && (
          <ControlledCheckBox name="kidCustomer" label="Ich bin ein Kind (unter 12 Jahre alt)" />
        )}

        <Stack gap={2} direction="row">
          <ControlledCheckBox name="silentBooking" label="Silent Cut" />
          <InfoButton
            title="Silent cut"
            text="Nach der Beratung und Besprechung des Schnitts geniesse ich meinen Schnitt heute gerne in Ruhe und verzichte auf Smalltalk."
          />
        </Stack>
      </Stack>

      <CouponInsert onValidate={setSelectedCoupon} />

      <Para size="sm" className="w-72 text-center text-zinc-700">
        Wir bitten Dich, 5 Minuten vor Deinem Termin im Salon zu erscheinen.
      </Para>

      <ActionButtons>
        <BookingPayment
          amount={computedPrice}
          data={{
            manual,
            customerName,
            phoneNumber,
            silentBooking,
            kidCustomer,
            couponCode: selectedCoupon?.code,
            storeSlug: store.slug,
          }}
        />
        <CancelButton onCancel={onCancel} />
      </ActionButtons>
    </StepContainer>
  );
}
